import { Inject, Injectable, Optional } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import {
	MatLegacyDialog as MatDialog,
	MatLegacyDialogConfig as MatDialogConfig,
} from "@angular/material/legacy-dialog";
import { Store } from "@ngrx/store";
import { AbstractTranslatableAlertService, JurisdictionPrefixPipe } from "@obg/ngx.core";
import { CoreContentState, TranslationGroup, TranslationSelector } from "@obg/ngx.core.content";
import { castArray, map as lodashMap } from "lodash-es";
import { of, Subscription } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { MAT_CORE_CONFIG } from "../const";
import { DialogService } from "../dialog/dialog.service";
import { MatCoreConfig } from "../model";

import { AlertComponent } from "./alert.component";
import { ALERT_DIALOG_ID } from "./alert.const";
import { AlertButton, AlertConfig, AlertOptions } from "./alert.model";

@Injectable()
export class TranslatableAlertService implements AbstractTranslatableAlertService {
	private config: AlertConfig;
	private isGeneralTranslationGroupNotUsed: boolean | undefined;
	private lastDialogRef: MatDialogRef<any, any> | undefined;

	constructor(
		@Optional() @Inject(MAT_CORE_CONFIG) coreConfig: MatCoreConfig,
		private store: Store<CoreContentState>,
		private translationSelector: TranslationSelector,
		private dialog: MatDialog,
		private jurisdictionPrefixPipe: JurisdictionPrefixPipe,
		private dialogService: DialogService
	) {
		this.config = coreConfig?.alert || {};
		this.isGeneralTranslationGroupNotUsed = coreConfig?.doNotUseGeneralTranslationGroup;
	}

	open$(options: AlertOptions, translationGroups: TranslationGroup[], interpolateParams?: object) {
		const keys = this.getAllKeys(options);

		return this.store.pipe(
			filter((state) => state.translation.isClientLoaded),
			this.translationSelector.getByKeys$(
				keys,
				this.isGeneralTranslationGroupNotUsed
					? translationGroups
					: [...translationGroups, TranslationGroup.General],
				interpolateParams
			),
			take(1),
			map(([title, message, redirectButtonText, ...buttonLabels]) => {
				options.buttons?.map((button) => (button.testIdSuffix = button.testIdSuffix || button.text));

				return {
					...options,
					title,
					message,
					redirectButtonText,
					buttons: lodashMap(options.buttons, (button: AlertButton, i: number) =>
						this.translateButton(button, buttonLabels[i])
					),
				};
			}),
			switchMap((opts) => {
				opts = {
					hideClose: this.config.hideClose,
					...opts,
					id: this.config.allowMultipleAlerts ? opts.id || ALERT_DIALOG_ID : ALERT_DIALOG_ID,
				};

				const existingSameDialog = this.dialog.openDialogs.find((d) => d.id === opts.id);
				const existingDialogMustBeInteracted = this.dialog.openDialogs.find(
					(d) => d._containerInstance._config.data.mustBeInteracted === true
				);
				const dialogOptions: MatDialogConfig = {
					maxHeight: this.config.defaultDialogMaxHeight,
					maxWidth: this.config.defaultDialogMaxWidth,
					panelClass: this.getPanelClass(opts.dialogConfig),
					disableClose: this.config.disableClose,
					...opts.dialogConfig,
				};
				const openDialog = () => {
					const alertComponent = options.customAlertComponent ?? AlertComponent;
					const dialogRef = this.dialog.open(alertComponent, {
						data: opts,
						...dialogOptions,
						id: opts.id,
					});

					if (opts.onClose) {
						dialogRef
							.beforeClosed()
							.pipe(take(1))
							// eslint-disable-next-line rxjs/no-ignored-subscription
							.subscribe(() => opts.onClose!());
					}

					this.dialogService.trackDialog(dialogRef);

					return dialogRef;
				};
				if (existingSameDialog && !existingSameDialog._containerInstance._config.data.mustBeInteracted) {
					existingSameDialog.close();
				}

				const showDialog$ = existingDialogMustBeInteracted
					? existingDialogMustBeInteracted.afterClosed()
					: existingSameDialog
						? existingSameDialog.afterClosed()
						: of(undefined);

				return showDialog$.pipe(take(1), map(openDialog));
			}),
			tap((dialogRef) => (this.lastDialogRef = dialogRef))
		);
	}

	open(options: AlertOptions, translationGroups: TranslationGroup[], interpolateParams?: object): Subscription {
		return this.open$(options, translationGroups, interpolateParams).subscribe();
	}

	close() {
		this.lastDialogRef?.close();
	}

	private getPanelClass(config: MatDialogConfig | undefined): string[] {
		return ["obg-mat-alert-dialog-panel", ...castArray(config?.panelClass || [])];
	}

	private getAllKeys({
		prefixJurisdiction,
		title = "",
		message = "",
		redirectButtonText = "",
		buttons = [],
	}: AlertOptions): string[] {
		return [title, message, redirectButtonText, ...lodashMap(buttons, (x: AlertButton) => x.text || "")].map(
			(key) => (key && prefixJurisdiction ? this.prefixJurisdiction(key) : key)
		);
	}

	private translateButton(button: AlertButton, label: string): AlertButton {
		return { ...button, text: label };
	}

	private prefixJurisdiction(key: string): string {
		return this.jurisdictionPrefixPipe.transform(key);
	}
}
